/**
 * useGoodsReceiptForm Hook
 *
 * Extracts all form logic from GoodsReceiptFormModal.
 * Handles state, validation, calculations, and actions.
 *
 * @see US-9.5 - Formulaire Reception
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import type { DateRange } from '@/components/ui';
import {
  useInitReceiptFromOrder,
  useCreateGoodsReceipt,
  useValidateGoodsReceipt,
} from './useGoodsReceipts';
import { useActiveSuppliers } from '@/features/suppliers/hooks';
import { VarianceReason } from '@bstock/shared';
import type {
  InitReceiptItemFromOrder,
  CreateGoodsReceiptDto,
  CreateGoodsReceiptItemDto,
  SupplierCatalogItemWithDetails,
} from '@bstock/shared';
import type { FormLineItem, FormError, FormTotals } from '../types';

interface UseGoodsReceiptFormParams {
  isOpen: boolean;
  orderId?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function useGoodsReceiptForm({
  isOpen,
  orderId,
  onClose,
  onSuccess,
}: UseGoodsReceiptFormParams) {
  // Mode: from order or free receipt
  const isFromOrder = Boolean(orderId);

  // Form state
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [receiptDateRange, setReceiptDateRange] = useState<DateRange>({
    start: new Date(),
    end: null,
  });
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [blNumber, setBlNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<FormLineItem[]>([]);
  const [isDefinitive, setIsDefinitive] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingBlFiles, setPendingBlFiles] = useState<File[]>([]);
  const [pendingInvoiceFiles, setPendingInvoiceFiles] = useState<File[]>([]);

  // Modals state
  const [showValidateConfirm, setShowValidateConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<FormError[]>([]);

  // Queries and mutations - ne charger les fournisseurs que si le modal est ouvert
  const { data: suppliers = [], isLoading: suppliersLoading } = useActiveSuppliers({
    enabled: isOpen,
  });
  const initFromOrderMutation = useInitReceiptFromOrder();
  const createMutation = useCreateGoodsReceipt();
  const validateMutation = useValidateGoodsReceipt();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSupplierId('');
      setSupplierName('');
      setOrderNumber('');
      setReceiptDateRange({ start: new Date(), end: null });
      setSupplierInvoiceRef('');
      setBlNumber('');
      setNotes('');
      setItems([]);
      setIsDefinitive(true);
      setHasUnsavedChanges(false);
      setIsInitialized(false);
      setErrors([]);
      setPendingBlFiles([]);
      setPendingInvoiceFiles([]);
    }
  }, [isOpen]);

  // Load from order if orderId is present
  useEffect(() => {
    if (isOpen && orderId && !isInitialized) {
      initFromOrderMutation.mutate(
        { purchaseOrderId: orderId },
        {
          onSuccess: (data) => {
            const receipt = data.receipt;
            setSupplierId(receipt.supplierId ?? '');
            setSupplierName(receipt.supplier?.name ?? '');
            setOrderNumber(receipt.purchaseOrder?.orderNumber ?? '');
            if (receipt.receiptDate) {
              setReceiptDateRange({
                start: new Date(receipt.receiptDate),
                end: null,
              });
            }
            // Map items from response
            setItems(
              data.items.map((item: InitReceiptItemFromOrder) => ({
                purchaseOrderItemId: item.purchaseOrderItemId,
                catalogItemId: item.catalogItemId,
                rawProductId: item.rawProductId,
                rawProductName: item.rawProductName,
                rawProductSku: item.rawProductSku,
                packagingName: item.packagingName,
                conversionToRecipeUnit: item.conversionToRecipeUnit,
                quantityOrdered: item.quantityOrdered,
                quantityAlreadyReceived: item.quantityAlreadyReceived,
                quantityRemaining: item.quantityRemaining,
                quantityReceived: item.suggestedQuantity,
                unitPriceExclTax: item.unitPriceExclTax,
                varianceReason: null,
                recipeUnitSymbol: item.recipeUnit.symbol,
              }))
            );
            setIsInitialized(true);
          },
        }
      );
    }
  }, [isOpen, orderId, isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Supplier options
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  );

  // Calculate totals
  const totals: FormTotals = useMemo(() => {
    let totalHT = 0;
    let itemsWithVariance = 0;

    items.forEach((item) => {
      totalHT += item.quantityReceived * item.unitPriceExclTax;
      // Variance is calculated against quantityRemaining (what we expect for THIS receipt)
      if (item.quantityRemaining !== null) {
        const variance = item.quantityReceived - item.quantityRemaining;
        if (variance !== 0) {
          itemsWithVariance++;
        }
      }
    });

    return { totalHT, itemsWithVariance };
  }, [items]);

  // Check if form is valid
  const isFormValid = useMemo(() => {
    return (
      supplierId &&
      receiptDateRange.start &&
      items.length > 0 &&
      items.some((item) => item.quantityReceived > 0)
    );
  }, [supplierId, receiptDateRange.start, items]);

  // Check if validation is allowed (all variances have reasons)
  // Variance is calculated against quantityRemaining (what we expect for THIS receipt)
  const canValidate = useMemo(() => {
    if (!isFormValid) return false;

    return items.every((item) => {
      if (item.quantityRemaining === null) return true;
      const variance = item.quantityReceived - item.quantityRemaining;
      if (variance !== 0) {
        return item.varianceReason !== null;
      }
      return true;
    });
  }, [isFormValid, items]);

  // Items with variance but no reason
  const itemsWithoutReason = useMemo(() => {
    return items.filter((item) => {
      if (item.quantityRemaining === null) return false;
      const variance = item.quantityReceived - item.quantityRemaining;
      return variance !== 0 && item.varianceReason === null;
    });
  }, [items]);

  // Handle quantity change
  const handleQuantityChange = useCallback((index: number, value: number) => {
    setItems((prev) => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        quantityReceived: Math.max(0, value),
      };
      return newItems;
    });
    setHasUnsavedChanges(true);
  }, []);

  // Handle variance reason change
  const handleVarianceReasonChange = useCallback(
    (index: number, value: string) => {
      setItems((prev) => {
        const newItems = [...prev];
        newItems[index] = {
          ...newItems[index],
          varianceReason: value ? (value as VarianceReason) : null,
        };
        return newItems;
      });
      setHasUnsavedChanges(true);
    },
    []
  );

  // Handle remove item (free receipt only)
  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  }, []);

  // Handle add item from catalog (free receipt only)
  const handleAddItem = useCallback(
    (catalogItem: SupplierCatalogItemWithDetails, variants: SupplierCatalogItemWithDetails[]) => {
      const packagingOptions = variants.length > 1
        ? variants.map((ci) => ({
            catalogItemId: ci.id,
            packagingName: ci.packaging.name,
            unitPriceExclTax: ci.currentPrice?.priceExclTax ?? 0,
            conversionToRecipeUnit: ci.conversionToRecipeUnit,
          }))
        : undefined;

      const newItem: FormLineItem = {
        purchaseOrderItemId: null,
        catalogItemId: catalogItem.id,
        rawProductId: catalogItem.rawProductId,
        rawProductName: catalogItem.rawProduct.name,
        rawProductSku: catalogItem.rawProduct.sku ?? '',
        packagingName: catalogItem.packaging.name,
        conversionToRecipeUnit: catalogItem.conversionToRecipeUnit,
        quantityOrdered: null,
        quantityAlreadyReceived: null,
        quantityRemaining: null,
        quantityReceived: 1,
        unitPriceExclTax: catalogItem.currentPrice?.priceExclTax ?? 0,
        varianceReason: null,
        recipeUnitSymbol: catalogItem.rawProduct.recipeUnit.symbol,
        packagingOptions,
      };

      setItems((prev) => [...prev, newItem]);
      setHasUnsavedChanges(true);
    },
    []
  );

  // Handle packaging change for free receipt items
  const handlePackagingChange = useCallback(
    (index: number, catalogItemId: string) => {
      setItems((prev) =>
        prev.map((item, i) => {
          if (i !== index) return item;
          const pkg = item.packagingOptions?.find((o) => o.catalogItemId === catalogItemId);
          if (!pkg) return item;
          return {
            ...item,
            catalogItemId,
            packagingName: pkg.packagingName,
            unitPriceExclTax: pkg.unitPriceExclTax,
            conversionToRecipeUnit: pkg.conversionToRecipeUnit,
          };
        })
      );
      setHasUnsavedChanges(true);
    },
    []
  );

  // Handle supplier change
  const handleSupplierChange = useCallback((value: string) => {
    setSupplierId(value);
    setHasUnsavedChanges(true);
  }, []);

  // Handle date change
  const handleDateChange = useCallback((range: DateRange) => {
    setReceiptDateRange(range);
    setHasUnsavedChanges(true);
  }, []);

  // Handle invoice ref change
  const handleInvoiceRefChange = useCallback((value: string) => {
    setSupplierInvoiceRef(value);
    setHasUnsavedChanges(true);
  }, []);

  // Handle BL number change
  const handleBlNumberChange = useCallback((value: string) => {
    setBlNumber(value);
    setHasUnsavedChanges(true);
  }, []);

  // Handle file selections (stored locally, sent with the create request)
  const handleBlFileSelect = useCallback((file: File) => {
    setPendingBlFiles((prev) => [...prev, file]);
    setHasUnsavedChanges(true);
  }, []);

  const handleInvoiceFileSelect = useCallback((file: File) => {
    setPendingInvoiceFiles((prev) => [...prev, file]);
    setHasUnsavedChanges(true);
  }, []);

  const handleRemovePendingBlFile = useCallback((index: number) => {
    setPendingBlFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemovePendingInvoiceFile = useCallback((index: number) => {
    setPendingInvoiceFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Handle isDefinitive change
  const handleIsDefinitiveChange = useCallback((value: boolean) => {
    setIsDefinitive(value);
    setHasUnsavedChanges(true);
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
    setHasUnsavedChanges(true);
  }, []);

  // Build DTO for API
  const buildCreateDto = useCallback((): CreateGoodsReceiptDto => {
    const itemsDto: CreateGoodsReceiptItemDto[] = items
      .filter((item) => item.quantityReceived > 0)
      .map((item) => ({
        purchaseOrderItemId: item.purchaseOrderItemId,
        catalogItemId: item.catalogItemId,
        rawProductId: item.rawProductId,
        quantityReceived: item.quantityReceived,
        unitPriceExclTax: item.unitPriceExclTax,
        varianceReason: item.varianceReason,
      }));

    return {
      purchaseOrderId: orderId || null,
      supplierId,
      receiptDate: receiptDateRange.start?.toISOString() ?? new Date().toISOString(),
      isDefinitive,
      supplierInvoiceRef: supplierInvoiceRef || null,
      notes: notes || null,
      items: itemsDto,
    };
  }, [items, orderId, supplierId, receiptDateRange.start, isDefinitive, supplierInvoiceRef, notes]);

  // Handle close with confirmation if unsaved changes
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // Save as draft
  const handleSaveDraft = useCallback(async () => {
    if (!isFormValid) return;

    setErrors([]);

    try {
      const dto = buildCreateDto();
      const files = {
        blFiles: pendingBlFiles.length > 0 ? pendingBlFiles : undefined,
        invoiceFiles: pendingInvoiceFiles.length > 0 ? pendingInvoiceFiles : undefined,
      };
      await createMutation.mutateAsync({ data: dto, files });
      setHasUnsavedChanges(false);
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by mutation
    }
  }, [isFormValid, buildCreateDto, createMutation, pendingBlFiles, pendingInvoiceFiles, onSuccess, onClose]);

  // Validate receipt
  const handleValidate = useCallback(async () => {
    if (!canValidate) {
      setErrors(
        itemsWithoutReason.map((item) => ({
          field: `variance-${item.rawProductId}`,
          message: `Motif requis pour ${item.rawProductName}`,
        }))
      );
      return;
    }

    setShowValidateConfirm(true);
  }, [canValidate, itemsWithoutReason]);

  const confirmValidate = useCallback(async () => {
    setShowValidateConfirm(false);

    try {
      // Create the receipt with files in a single request
      const dto = buildCreateDto();
      const files = {
        blFiles: pendingBlFiles.length > 0 ? pendingBlFiles : undefined,
        invoiceFiles: pendingInvoiceFiles.length > 0 ? pendingInvoiceFiles : undefined,
      };
      const receipt = await createMutation.mutateAsync({ data: dto, files });

      // Then validate it
      await validateMutation.mutateAsync(receipt.id);

      setHasUnsavedChanges(false);
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by mutations
    }
  }, [buildCreateDto, createMutation, validateMutation, pendingBlFiles, pendingInvoiceFiles, onSuccess, onClose]);

  const confirmCancel = useCallback(() => {
    setShowCancelConfirm(false);
    onClose();
  }, [onClose]);

  const closeValidateConfirm = useCallback(() => {
    setShowValidateConfirm(false);
  }, []);

  const closeCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  // Computed mutation states
  const isSaving = createMutation.isPending;
  const isValidating = validateMutation.isPending;
  const isLoading = isFromOrder && initFromOrderMutation.isPending;
  const isError = isFromOrder && initFromOrderMutation.isError;

  return {
    // Mode
    isFromOrder,

    // Form state
    supplierId,
    supplierName,
    orderNumber,
    receiptDateRange,
    supplierInvoiceRef,
    blNumber,
    notes,
    items,
    isDefinitive,

    // Derived state
    supplierOptions,
    suppliersLoading,
    totals,
    isFormValid,
    canValidate,
    errors,

    // Modal state
    showValidateConfirm,
    showCancelConfirm,

    // Mutation states
    isSaving,
    isValidating,
    isLoading,
    isError,

    // Handlers
    handleQuantityChange,
    handleVarianceReasonChange,
    handleRemoveItem,
    handleAddItem,
    handlePackagingChange,
    handleSupplierChange,
    handleDateChange,
    handleInvoiceRefChange,
    handleBlNumberChange,
    handleIsDefinitiveChange,
    handleNotesChange,
    pendingBlFiles,
    pendingInvoiceFiles,
    handleBlFileSelect,
    handleInvoiceFileSelect,
    handleRemovePendingBlFile,
    handleRemovePendingInvoiceFile,
    handleClose,
    handleSaveDraft,
    handleValidate,
    confirmValidate,
    confirmCancel,
    closeValidateConfirm,
    closeCancelConfirm,
  };
}
